// What the person reads when a call fails (IMPROVE-BO-01: legacy dropped most 4xx silently). One sentence per contract
// error code; anything unlisted falls back to the generic line. Legacy copy where legacy had a message.
const MESSAGES: Readonly<Record<string, string>> = {
  unauthenticated: 'Your session has expired. Please log in again.',
  forbidden: "You don't have permission to do that.",
  validation_failed: 'Please check the highlighted fields.',
  needs_internet: 'Needs internet: this works once the store is back online.',
  storage_full: "The store server's disk is full, so it can't save changes right now. Try again once space is freed.",
  network_error: "Can't reach the server. Check the connection and try again.",
  unexpected_response: 'Something went wrong. Please try again.',
  internal_error: 'Something went wrong. Please try again.',
  idempotency_conflict: 'That change was already sent.',
  'catalog.invalid': 'This change was refused.',
  'catalog.not_found': 'It was deleted somewhere else. Refresh to see the latest.',
  'config.cloud_authoritative': 'This setting is managed by Radish and changes only online.',
  'config.proposal_required': 'This is managed at the main location; the change was sent for approval.',
  'settings.invalid': 'That value is not allowed.',
  'settings.unknown_key': 'This setting is not available.',
  'billing.stripe_not_configured': "Billing isn't connected to Stripe on this server, so plans can't be published here.",
  'billing.location_not_found': 'This location could not be found.',
  'billing.already_on_plan': 'This location is already on a plan.',
  'billing.not_on_plan': 'This location is not on a plan.',
  'billing.version_not_offered': 'That plan version is not offered any more.',
  'billing.feature_not_offered': 'That add-on is not offered on this plan.',
  'billing.price_blank': 'A price on this plan is not set yet.',
  'billing.manual_subscriptions_unconfirmed': 'Confirm each subscription billed by hand is being cancelled first.',
  'billing.stripe_customer_not_found': 'No such customer in Stripe.',
  'billing.grant_refused': "That adjustment can't be granted.",
  'billing.free_time_over': 'The free time is over, so the location can only be cancelled now.',
  'billing.adjustment_not_found': 'That adjustment no longer exists.',
  'billing.invoice_not_found': 'That invoice could not be found.',
  'billing.no_customer': 'There is no card to update yet: your Account Representative sets up billing first.',
  'billing.card_update_incomplete': 'The card was not saved. Please try again.',
  'billing.merchant_not_on_plan': 'Your Account Representative handles billing for this location.',
  'labor.not_found': 'This timecard no longer exists.',
  'labor.overlaps_timecard': 'This timecard overlaps another timecard for the same employee.',
  'labor.spans_business_day_start': 'A timecard cannot run past the start of the next business day.',
  'labor.rule_violation': 'This change breaks a timecard rule.',
  'labor.employee_cannot_clock_in': 'This employee cannot work at this location.',
  'labor.pin_taken': 'PIN is in use, please provide a different PIN',
  'labor.job_role_name_taken': 'The name has already been taken.',
  'labor.job_role_not_found': 'That role no longer exists.',
  'labor.employee_not_found': 'This employee no longer exists.',
  'labor.time_off_request_not_found': 'Time-off request not found.',
  'labor.schedule_template_not_found': 'Schedule template not found.',
  'labor.schedule_exists': 'A schedule already exists for this location and target week.',
  'floor.invalid': 'This change was refused.',
  'floor.not_found': 'Floor plan not found',
  'online.invalid': 'There was an error updating your store',
  'online.store_not_found': 'Online store not found',
  'online.gateway_restricted': 'Only Radish staff and resellers can change API keys.',
  'online.voice_conversation_not_found': 'Could not load that conversation.',
  'online.qr_code_not_found': 'That QR code no longer exists.',
  'online.qr_code_invalid': 'This QR code could not be saved.',
  'identity.role_not_found': 'This role no longer exists.',
  'identity.system_role_locked': 'System roles cannot be modified.',
  'identity.member_not_found': 'This user is no longer assigned to this location.',
  'identity.own_membership': 'You cannot change your own role or remove yourself from this location.',
  'identity.last_admin': 'Cannot remove the only admin from this location.',
  'identity.account_member': 'This user has access to the whole account; manage them on the account.',
  'identity.already_member': 'This user is already assigned to this location.',
  'identity.already_invited': 'The user is already invited to this location',
  'identity.invite_mail_limited': 'Too many invitations sent. Please try again later.',
  'identity.invite_not_found': 'This invitation no longer exists.',
  // Legacy UserRequest's copy for a taken email; Radish Next's passwords are 8 or more characters (legacy's admin form took 6).
  'identity.email_taken': 'The email has already been taken.',
  'identity.password_invalid': 'The password must be at least 8 characters.',
  'identity.user_exists': 'This user already exists.',
};

const RULES: Readonly<Record<string, string>> = {
  true_margin_inclusive_products: 'TrueMargin pricing cannot include tax-inclusive items.',
  // catalog.invalid: legacy DiningOptionController@update's 422, walked 2026-10-14 (D1 round 39, a red toast; the dialog stays open)
  dining_option_default_required: 'You need to have 1 default dining option set',
  // floor.invalid (legacy FloorPlanItemStoreRequest / ZoneStoreRequest / FreeTableStoreRequest copy)
  table_number_taken: 'The table number has already been taken in this zone.',
  table_number_out_of_range: 'Numbers must be between 1 and 999',
  zone_required: 'Select a zone for every table.',
  prefix_taken: 'The prefix has already been taken.',
  prefix_required: 'The prefix field is required.',
  prefix_too_long: 'Must be one to three letters, (e.g., A or BAR)',
  name_required: 'Floor name is required',
  // online.invalid (legacy OnlineStoreRequest copy where it had one)
  display_name_required: 'Display Name is required',
  email_required: 'Email is required',
  phone_required: 'Phone is required',
  subdomain_taken: 'The subdomain has already been taken.',
  one_default_prep_time: 'Choose one default prep time.',
  prep_time_required: 'You cannot delete the last prep time.',
  one_active_banner: 'Only one banner can be active.',
  closure_ends_before_start: 'The end time cannot be before the start time',
  second_window_after_first: 'The selected time should be after the previous time',
  // labor.time_off_refused (legacy time-off actions' copy)
  overlaps: 'You already have a time-off request for overlapping dates.',
  // Legacy's back office refuses it in TimeOffRequestFormRequest's `after_or_equal:start_date` (modules/Scheduling), so it says Laravel's
  // sentence, not the register's (B1570).
  end_before_start: 'The end date must be a date after or equal to start date.',
  not_pending: 'Only pending requests can be reviewed.',
};

/** A rule whose words depend on the refusal: these win over RULES for their code. */
const CODE_RULES: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  // labor.schedule_refused, every rule of the contract's ScheduleRefused (B1554, L1030). Legacy's interceptor drops a 400, so
  // there Publish on a week with no shifts did nothing visible; Next says legacy's own sentence (PublishScheduleAction,
  // DeleteScheduleAction, SyncShiftBreaks). Legacy names a retired break; the refusal carries only its rule, so Next says
  // it without the name. Legacy has no week-start or outside-week refusal: those are Next's own words.
  'labor.schedule_refused': {
    no_shifts: 'Cannot publish a schedule with no shifts.',
    not_draft: 'Only draft schedules can be published.',
    published: 'Cannot delete a published schedule.',
    too_many_breaks: 'A shift can plan at most 10 breaks.',
    breaks_exceed_shift: 'Planned breaks must total less than the length of the shift.',
    break_retired: 'This break has been deleted and cannot be added to a shift.',
    not_week_start: "This date doesn't start a schedule week.",
    outside_week: "This shift's date is outside its schedule's week.",
    // Legacy ShiftAssigneeValidator's sentence (API 8212e198b).
    employee_inactive: 'This employee is no longer active.',
    // B1673: Next's own words (legacy let an id not on the staff through).
    employee_not_on_staff: "This employee isn't on this location's staff.",
  },
  // labor.shift_claim_refused (legacy OpenShiftClaimValidator and ReviewOpenShiftClaimAction copy)
  'labor.shift_claim_refused': {
    claims_disabled: 'Open shift claims are not enabled for this location.',
    not_open: 'This shift has already been assigned.',
    started: 'Cannot claim a shift that has already started.',
    overlaps: "Cannot claim this shift: it overlaps one of the employee's shifts.",
    already_requested: 'You already have a pending request for this shift.',
    not_pending: 'Only pending claim requests can be reviewed.',
    time_off: 'Cannot claim this shift: the employee has approved time off that day.',
    // Remove of a request already gone (legacy's findOrFail answers Laravel's 404 sentence, which the Back Office drops).
    not_found: 'This request was withdrawn or removed.',
  },
  // labor.shift_swap_refused (legacy ShiftSwapController, ReviewShiftSwapAction, ExecuteShiftSwapAction copy)
  'labor.shift_swap_refused': {
    claims_disabled: 'Open shift claims are not enabled for this location.',
    open_shift: 'An open shift has nobody to release it; it can be claimed as it is.',
    unpublished: 'This shift is not on a published schedule yet.',
    started: 'This shift has already started.',
    already_requested: 'This shift already has a request waiting on it.',
    own_shift: 'A released shift cannot be given back to the employee releasing it.',
    not_on_roster: "That employee is not on this location's roster.",
    party_off_roster: "One of the employees is no longer on this location's roster.",
    not_live: 'Only pending or accepted swap requests can be reviewed.',
    not_accepted: 'The coworker has not accepted this swap yet.',
    swap_fixed: 'A swap is approved as the two employees agreed it, or denied.',
    no_recipient: 'A receiving employee must be assigned before approving.',
    shift_gone: 'This shift no longer exists.',
    offered_gone: 'The shift offered in return no longer exists.',
    not_requesters: 'This shift is no longer assigned to the employee who requested the swap.',
    offered_not_recipients: 'The shift offered in return is no longer assigned to the receiving employee.',
    offered_started: 'The shift offered in return has already started.',
    overlaps: 'Cannot complete this swap: the employee is already scheduled then.',
    time_off: 'Cannot complete this swap: the employee has approved time off that day.',
  },
};

/** "9:00am" (legacy ScheduleConflictDetector `to12h`). */
const to12h = (time: string): string => {
  const h = Number(time.slice(0, 2));
  return `${String(h === 0 ? 12 : h > 12 ? h - 12 : h)}:${time.slice(3, 5)}${h >= 12 ? 'pm' : 'am'}`;
};

/** Legacy's sentences that name the shift or the day (ExecuteShiftSwapAction::assertCanTake). */
function swapDetail(rule: string, details: Record<string, unknown>): string | undefined {
  const c = details.conflict as { businessDate?: unknown; startTime?: unknown; endTime?: unknown } | undefined;
  if (rule === 'overlaps' && typeof c?.businessDate === 'string' && typeof c.startTime === 'string' && typeof c.endTime === 'string') {
    return `Cannot complete this swap: Employee is already scheduled from ${to12h(c.startTime)} to ${to12h(c.endTime)} on ${c.businessDate}.`;
  }
  if (rule === 'time_off' && typeof details.date === 'string') return `Cannot complete this swap: the employee has approved time off on ${details.date}.`;
  return undefined;
}

export function messageFor(code: string, details: unknown): string {
  const rule = typeof details === 'object' && details !== null && 'rule' in details ? String((details as { rule: unknown }).rule) : undefined;
  const detailed = rule === undefined ? undefined
    : code === 'labor.shift_swap_refused' ? swapDetail(rule, details as Record<string, unknown>)
      // B1600: legacy OpenShiftClaimValidator names the day (a29bc0426).
      : code === 'labor.shift_claim_refused' && rule === 'time_off' && typeof (details as { date?: unknown }).date === 'string'
        ? `Cannot claim this shift: the employee has approved time off on ${String((details as { date: string }).date)}.` : undefined;
  if (detailed !== undefined) return detailed;
  const scoped = rule === undefined ? undefined : CODE_RULES[code]?.[rule];
  if (scoped !== undefined) return scoped;
  if (rule !== undefined && RULES[rule] !== undefined) return RULES[rule];
  return MESSAGES[code] ?? MESSAGES['internal_error'] ?? 'Something went wrong.';
}
